import { get, useFormContext, Controller } from 'react-hook-form';
import { useDebouncedSave } from '../hooks/useDebounce';

export default function FormInput(props: {
  fieldName: string;
  rows?: number;
  required?: string;
  options?: any[];
  optionLabel?: string;
  optionLabelFn?: (option: any) => string;
  emptyOption?: string;
  customClass?: string;
  inputType?: string;
  defaultValue?: string | number;
  // Debounced save props
  enableDebouncedSave?: boolean;
  debouncedSaveFn?: (value: string) => Promise<void>;
  setIsSaving?: (isSaving: boolean) => void;
}) {
  const {
    fieldName,
    rows,
    required,
    options,
    optionLabel,
    optionLabelFn,
    emptyOption,
    customClass = '',
    inputType = 'text',
    defaultValue,
    enableDebouncedSave = false,
    debouncedSaveFn,
    setIsSaving,
  } = props;

  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();

  // Use the new useDebouncedSave hook
  useDebouncedSave(fieldName, debouncedSaveFn, {
    setIsSaving,
    enabled: enableDebouncedSave,
  });

  const error = get(errors, fieldName);
  const className = `cr-rounded-md cr-box-border cr-text-blue-900 cr-font-light
    cr-w-full cr-max-w-full cr-h-[26px] ${customClass} ${
    error
      ? 'cr-border-red-500 cr-border-2 cr-outline-red-500 cr-outline-2'
      : 'cr-border cr-border-blue-900 focus:cr-outline-2 focus:cr-outline-cirrusBlue-400'
  } ${inputType === 'number' ? 'cr-no-spinner' : ''}`;
  const registration = register(fieldName, { required });

  const optionsList =
    options &&
    (optionLabel || optionLabelFn) &&
    options.map((o: any) => (
      <option key={o.id} value={o.id} disabled={o.disabled}>
        {optionLabelFn ? optionLabelFn(o) : o[optionLabel!]}
      </option>
    ));

  return (
    <div>
      {rows ? (
        <textarea rows={rows} className={className} defaultValue={defaultValue} {...registration} />
      ) : options ? (
        <Controller
          name={fieldName}
          control={control}
          rules={{ required }}
          defaultValue={defaultValue || ''}
          render={({ field }) => (
            <select
              {...field}
              value={field.value ?? ''} // Convert null to empty string
              className={className}
              onChange={(e) => {
                field.onChange(e);
              }}
            >
              {emptyOption && <option value="">{emptyOption}</option>}
              {optionsList}
            </select>
          )}
        />
      ) : (
        <input className={className} defaultValue={defaultValue} {...registration} type={inputType} />
      )}
      {error?.message && <p className="cr-text-[12px] cr-text-red-500">{String(error.message)}</p>}
    </div>
  );
}
